import React from 'react';
import EthicsPage from '../pages/EthicsPage';
import IntegrityTrainingPage from '../pages/IntegrityTrainingPage';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import SectionWrapper from '../components/SectionWrapper';
import ScrollToHash from '../components/ScrollToHash';
import SessionTimeout from '../components/SessionTimeout';
import FloatingMenu from '../components/FloatingMenu';
import CookieConsent from '../components/CookieConsent';
import ErrorBoundary from '../components/ErrorBoundary';
import ProtectedRoute from '../components/ProtectedRoute';

import NewsPage from '../pages/NewsPage';
import GovernancePage from '../pages/GovernancePage';
import GovernanceReaderPage from '../pages/GovernanceReaderPage';
import PartnerPage from '../pages/PartnerPage';
import PartnersPage from '../pages/PartnersPage';
import ClientsPage from '../pages/ClientsPage';
import LoginPage from '../pages/LoginPage';
import AdminPanel from '../pages/AdminPanel';
import SearchPage from '../pages/SearchPage';
import ArticlePage from '../pages/ArticlePage';
import DesignSystemPage from '../pages/DesignSystemPage';
import NotFoundPage from '../pages/NotFoundPage';
import CareersPage from '../pages/CareersPage';
import HealthcarePayersBriefingPage from '../pages/HealthcarePayersBriefingPage';
import HospitalsBriefingPage from '../pages/HospitalsBriefingPage';
import SupermarketsBriefingPage from '../pages/SupermarketsBriefingPage';

import useIsMobile from '../shared/hooks/useIsMobile';
import { HomeFeature } from '../features/home';
import { AllNewsFeature } from '../features/all-news';
import { OverviewFeature } from '../features/overview';
import { PurposeFeature } from '../features/purpose';
import { ContactFeature } from '../features/contact';

const RouteErrorBoundary = ({ children }) => {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.key || location.pathname}>{children}</ErrorBoundary>;
};

const FloatingMenuGate = () => {
  const location = useLocation();
  const isMobile = useIsMobile();

  if (isMobile) return null;

  const hiddenPaths = new Set(['/login', '/portal-cliente', '/fale-conosco', '/canal-de-etica', '/capacitacao/integridade', '/trabalhe-conosco', '/design-system']);
  if (hiddenPaths.has(location.pathname)) return null;
  if (
    location.pathname.startsWith('/insights/healthcare-payers') ||
    location.pathname.startsWith('/insights/hospitals') ||
    location.pathname.startsWith('/insights/supermarkets')
  ) return null;
  return <FloatingMenu />;
};

const AppRoutes = () => {
  const showImpactSection = true;
  const isMobile = useIsMobile();

  return (
    <RouteErrorBoundary>
      <ScrollToHash />
      <SessionTimeout />
      <FloatingMenuGate />

      <Routes>
        <Route path="/" element={<HomeFeature showImpactSection={showImpactSection} />} />
        <Route path="/news" element={<SectionWrapper id="all-news" mobileSafe={isMobile}><AllNewsFeature /></SectionWrapper>} />
        <Route path="/visao-geral" element={<OverviewFeature />} />
        <Route path="/nossa-historia" element={<Navigate to="/visao-geral" replace />} />
        <Route path="/proposito" element={<PurposeFeature />} />

        <Route path="/news/:id" element={<SectionWrapper id="news-item" mobileSafe={isMobile}><NewsPage /></SectionWrapper>} />

        <Route path="/login" element={<SectionWrapper id="login" mobileSafe={isMobile}><LoginPage /></SectionWrapper>} />
        <Route path="/portal-cliente" element={<SectionWrapper id="portal-cliente" mobileSafe={isMobile}><LoginPage isClient={true} /></SectionWrapper>} />

        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <SectionWrapper id="admin" mobileSafe={isMobile}>
                <AdminPanel />
              </SectionWrapper>
            </ProtectedRoute>
          }
        />

        <Route path="/search" element={<SectionWrapper id="search" mobileSafe={isMobile}><SearchPage /></SectionWrapper>} />
        <Route path="/fale-conosco" element={<ContactFeature />} />
        <Route path="/canal-de-etica" element={<EthicsPage />} />
        <Route path="/capacitacao/integridade" element={<IntegrityTrainingPage />} />
        <Route path="/insights/healthcare-payers" element={<HealthcarePayersBriefingPage />} />
        <Route path="/insights/hospitals" element={<HospitalsBriefingPage />} />
        <Route path="/insights/supermarkets" element={<SupermarketsBriefingPage />} />
        <Route path="/trabalhe-conosco" element={<SectionWrapper id="careers" mobileSafe={isMobile}><CareersPage /></SectionWrapper>} />
        <Route path="/termos-de-uso" element={<Navigate to="/governanca/terms-of-use" replace />} />
        <Route path="/politica-de-privacidade" element={<Navigate to="/governanca/privacy-policy" replace />} />
        <Route path="/governanca" element={<GovernancePage />} />
        <Route
          path="/governanca/:slug"
          element={
            <SectionWrapper id="governance-reader" mobileSafe={isMobile}>
              <GovernanceReaderPage />
            </SectionWrapper>
          }
        />
        <Route path="/parceiros" element={<PartnersPage />} />
        <Route path="/parceiros/:id" element={<SectionWrapper id="parceiro" mobileSafe={isMobile}><PartnerPage /></SectionWrapper>} />
        <Route path="/clientes" element={<ClientsPage />} />
        <Route path="/artigo/:slug" element={<SectionWrapper id="artigo" mobileSafe={isMobile}><ArticlePage /></SectionWrapper>} />
        <Route path="/design-system" element={<DesignSystemPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      <CookieConsent />
    </RouteErrorBoundary>
  );
};

export default AppRoutes;
